import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, Copy, Check, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  copied: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
    copied: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleCopyDetails = async () => {
    if (!this.state.error) return;

    const errorDetails = `
=== ACONTURS Litoral - Relatório de Erro ===
Data/Hora: ${new Date().toISOString()}
URL Atual: ${window.location.href}
User Agent: ${navigator.userAgent}

Erro: ${this.state.error.name}: ${this.state.error.message}
Stack Trace:
${this.state.error.stack || 'Sem stack trace disponível'}

Component Stack:
${this.state.errorInfo?.componentStack || 'Sem stack de componentes disponível'}
===========================================
    `.trim();

    try {
      await navigator.clipboard.writeText(errorDetails);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 3000);
    } catch (err) {
      console.error('Falha ao copiar erro para a área de transferência', err);
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-900 text-slate-100 p-6 selection:bg-rose-500 selection:text-white">
          <div className="w-full max-w-2xl bg-slate-800 rounded-2xl shadow-2xl border border-slate-700/50 p-6 md:p-8 space-y-6 animate-in fade-in zoom-in duration-300">
            {/* Header / Alert Icon */}
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-rose-500/10 text-rose-400 rounded-full ring-8 ring-rose-500/5">
                <AlertTriangle className="h-10 w-10" />
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                  Ops! Algo deu errado
                </h1>
                <p className="text-slate-400 max-w-md text-sm md:text-base leading-relaxed">
                  O aplicativo encontrou um erro inesperado. Você pode recarregar a página ou copiar os detalhes técnicos para enviar ao suporte.
                </p>
              </div>
            </div>

            {/* Error Message Box */}
            <div className="bg-slate-900/60 border border-slate-700/30 rounded-xl p-4 flex items-start space-x-3">
              <div className="text-rose-400 font-mono text-xs select-none mt-0.5">[ERRO]</div>
              <div className="flex-1 space-y-1">
                <div className="text-sm font-semibold text-rose-300 font-mono break-all">
                  {this.state.error?.name || 'Error'}
                </div>
                <div className="text-xs text-slate-300 font-mono break-words">
                  {this.state.error?.message || 'Ocorreu um erro desconhecido.'}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-indigo-600/20 duration-200"
              >
                <RotateCcw className="h-4 w-4" />
                Recarregar Página
              </button>

              <button
                onClick={this.handleCopyDetails}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 font-medium rounded-xl transition-all duration-200 border ${
                  this.state.copied
                    ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                    : 'bg-slate-700/50 hover:bg-slate-700 border-slate-600/50 text-slate-200'
                }`}
              >
                {this.state.copied ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-400" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copiar Detalhes do Erro
                  </>
                )}
              </button>
            </div>

            {/* Technical Details Accordion */}
            <div className="border-t border-slate-700/40 pt-4">
              <button
                onClick={() => this.setState((prev) => ({ showDetails: !prev }))}
                className="w-full flex items-center justify-between py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors"
              >
                <span>Detalhes Técnicos / Stack Trace</span>
                {this.state.showDetails ? (
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                )}
              </button>

              {this.state.showDetails && (
                <div className="mt-3 bg-slate-950 border border-slate-800 rounded-xl p-4 max-h-60 overflow-y-auto font-mono text-[10px] md:text-xs text-slate-350 leading-relaxed scrollbar-thin scrollbar-thumb-slate-800 scrollbar-track-transparent">
                  <div className="text-indigo-400 font-semibold mb-2"># Error Stack:</div>
                  <pre className="whitespace-pre-wrap break-all text-slate-400">
                    {this.state.error?.stack || 'Sem detalhes de stack.'}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <div className="text-indigo-400 font-semibold mt-4 mb-2"># Component Stack:</div>
                      <pre className="whitespace-pre-wrap break-all text-slate-500">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
